import * as Sentry from '@sentry/react';
import React from 'react';
import { createRoot } from 'react-dom/client';
import { Provider } from 'react-redux';
import configureStore from './redux/store';
import App from './App';
import './index.css';
import './App.css';
import './common.css';
import { HelmetProvider } from 'react-helmet-async';
import { isSafariBrowser } from './util_functions';

const shouldEnableSentry = () => {
  if (
    import.meta.env.VITE_APP_STAGE !== 'production' ||
    import.meta.env.SSR ||
    window.location.hostname.includes('local')
  ) {
    return false;
  }

  return true;
};

Sentry.init({
  dsn: 'https://e341f8dd761adfe882abb53a5a6d1df9@o4507713531936768.ingest.us.sentry.io/4507854393507840',
  integrations: [
    // Sentry.captureConsoleIntegration(),
  ],
  environment: import.meta.env.VITE_APP_STAGE,
  autoSessionTracking: true,
  enabled: shouldEnableSentry()
});

const store = configureStore();

const normalizeChatRole = (value) => {
  if (typeof value !== 'string') return '';
  const normalized = value.trim().toLowerCase();
  return normalized === 'teacher' || normalized === 'student' ? normalized : '';
};

export default class NewApp extends React.Component {
  componentDidMount() {
    if (navigator.serviceWorker) {
      try {
        navigator.serviceWorker.addEventListener('message', (event) => {
          //   if (event.data?.type === 'CHAT_CLICK') {
          //     const { role, channelUuid } = event.data;

          //     const redirectUrl = role === 'teacher' ? '/teacher_chat' : '/student_chat';
          //     if (window.location.pathname !== redirectUrl) {
          //       window.location.href = redirectUrl;
          //     }
          //   }

          const { type, role, channelUuid, targetPath } = event.data || {};
          const normalizedRole = normalizeChatRole(role);
          // Only handle explicit CHAT_CLICK with a known role so we never redirect the wrong tab (e.g. teacher -> student_chat).
          if (type !== 'CHAT_CLICK' || !channelUuid || !normalizedRole) return;

          const redirectUrl =
            targetPath || (normalizedRole === 'teacher' ? '/teacher_chat' : '/student_chat');
          const qp = channelUuid
            ? `?from=push&channel=${encodeURIComponent(channelUuid)}`
            : '?from=push';
          const targetUrl = `${redirectUrl}${qp}`;
          const isSameChatType = window.location.pathname.startsWith(redirectUrl);

          if (isSameChatType) {
            // Same chat type: stay on this tab, store for mount or dispatch event
            sessionStorage.setItem(
              'pendingChatOpen',
              JSON.stringify({ role: normalizedRole, channelUuid, ts: Date.now() })
            );
            window.dispatchEvent(new CustomEvent('chat:open', { detail: { channelUuid } }));
          } else if (
            window.location.pathname.startsWith('/student_chat') ||
            window.location.pathname.startsWith('/teacher_chat')
          ) {
            // Safari is more reliable when we reuse the current tab for chat routes.
            if (isSafariBrowser()) {
              sessionStorage.setItem(
                'pendingChatOpen',
                JSON.stringify({ role: normalizedRole, channelUuid, ts: Date.now() })
              );
              window.location.assign(targetUrl);
              return;
            }

            // Care team has the other chat type open (e.g. student chat open, teacher notification):
            // open target chat in a new tab instead of replacing current tab
            const newWindow = window.open(targetUrl, '_blank');
            if (newWindow) newWindow.focus();
          } else {
            // Not on a chat page: redirect this tab to the target chat
            sessionStorage.setItem(
              'pendingChatOpen',
              JSON.stringify({ role: normalizedRole, channelUuid, ts: Date.now() })
            );
            window.location.assign(targetUrl);
          }
        });
      } catch (error) {
        console.log('Error in navigator block in main', error);
      }
    }
  }

  render() {
    return (
      <Sentry.ErrorBoundary fallback={<div>Loading</div>}>
        <HelmetProvider>
          <Provider store={store}>
            <App />
          </Provider>
        </HelmetProvider>
      </Sentry.ErrorBoundary>
    );
  }
}

const container = document.getElementById('root');
const root = createRoot(container);
root.render(<NewApp />);
